// src/Pages/Feels/FeelsShuffle/index.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAPI } from '../../../Contexts/APIContext';
import styles from './index.module.css';

const STORAGE_KEY = 'feelsShuffleMuted';
const BATCH_SIZE = 20;
const VIDEO_CAP = 500;

const FeelsShuffle = () => {
  const api = useAPI();
  const videoRef = useRef(null);

  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [allVideosFetched, setAllVideosFetched] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);

  const [isMuted, setIsMuted] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) ?? false;
    } catch {
      return false;
    }
  });

  // Sync localStorage whenever mute toggles
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(isMuted));
    } catch {}
  }, [isMuted]);

  // Ensure each new <video> picks up our mute preference
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, currentIndex]);

  // Listen for native mute/unmute (so controls still work)
  useEffect(() => {
    const vidEl = videoRef.current;
    if (!vidEl) return;
    const onVolumeChange = () => setIsMuted(vidEl.muted);
    vidEl.addEventListener('volumechange', onVolumeChange);
    return () => vidEl.removeEventListener('volumechange', onVolumeChange);
  }, [currentIndex]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const fetchData = useCallback(async (page = 1, isInit = false) => {
    try {
      if (isInit) setLoading(true);
      else setFetchingMore(true);

      const res = await api.getFeels('published', page, BATCH_SIZE, 'recent');
    //   console.log('API Response for page', page, ':', res);

      if (!res?.feels || !Array.isArray(res.feels)) {
        throw new Error('Invalid response');
      }

      const totalItems = res.pagination?.total_items ?? res.feels_count ?? 0;
      const pages = Math.ceil(totalItems / BATCH_SIZE);
      setTotalPages(pages);

      setVideos((prev) => {
        if (isInit) return res.feels;
        const seen = new Set(prev.map((v) => v.f_id));
        return [...prev, ...res.feels.filter((v) => !seen.has(v.f_id))];
      });

      if (page >= pages) setAllVideosFetched(true);
      if (isInit) setCurrentIndex(0);
      setCurrentPage(page);
    } catch (err) {
      console.error(err);
      setError('Failed to load videos. Try again later.');
    } finally {
      if (isInit) setLoading(false);
      else setFetchingMore(false);
    }
  }, [api]);

  const checkAndLoadMore = useCallback(() => {
    if (fetchingMore || allVideosFetched) return;
    const threshold = Math.max(2, Math.floor(BATCH_SIZE * 0.2));
    if (
      videos.length &&
      currentIndex >= videos.length - threshold &&
      currentPage < totalPages
    ) {
      fetchData(currentPage + 1, false);
    }
  }, [fetchingMore, allVideosFetched, videos.length, currentIndex, currentPage, totalPages, fetchData]);

  const goToNext = useCallback(() => {
    if (!videos.length) return;

    if (videos.length >= VIDEO_CAP && currentIndex >= VIDEO_CAP - 1) {
      setAllVideosFetched(false);
      fetchData(1, true);
      return;
    }

    const atLast = currentIndex === videos.length - 1;
    if (atLast) {
      if (allVideosFetched) setCurrentIndex(0);
      else checkAndLoadMore();
    } else {
      setCurrentIndex((i) => i + 1);
      if (currentIndex >= videos.length - threshold) checkAndLoadMore();
    }
  }, [videos, currentIndex, allVideosFetched, fetchData, checkAndLoadMore]);

  const goToPrev = useCallback(() => {
    if (!videos.length) return;
    setCurrentIndex((i) => (i === 0 ? videos.length - 1 : i - 1));
  }, [videos]);

  useEffect(() => { fetchData(1, true); }, [fetchData]);

  // Auto-advance when video ends
  useEffect(() => {
    const vidEl = videoRef.current;
    if (!vidEl) return;
    const onEnded = () => goToNext();
    vidEl.addEventListener('ended', onEnded);
    return () => vidEl.removeEventListener('ended', onEnded);
  }, [videos, goToNext]);

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.titleContainer}>
          <h1 className={styles.title}>Recently Created Feels</h1>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <p className={styles.loadingText}>Loading videos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.titleContainer}>
          <h1 className={styles.title}>Recently Created Feels</h1>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <p className={styles.errorText}>{error}</p>
          <button onClick={() => fetchData(1, true)} className={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!videos.length) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.titleContainer}>
          <h1 className={styles.title}>Recently Created Feels</h1>
        </div>
        <div className={styles.loadingContainer}>
          <p className={styles.noVideosText}>No videos available</p>
        </div>
      </div>
    );
  }

  const vid = videos[currentIndex];

  return (
    <div className={styles.wrapper}>
      <div className={styles.titleContainer}>
        <h1 className={styles.title}>Recently Created Feels</h1>
      </div>

      <div className={styles.contentWrapper}>
        {/* Song Info */}
        <div className={styles.infoContainer}>
          <div className={styles.songInfo}>
            <h2 className={styles.songTitle}>{vid.song_name}</h2>
            <h3 className={styles.artistName}>{vid.song_artist_name}</h3>
          </div>
        </div>

        {/* Video Column */}
        <div className={styles.videoColumn}>
          <button className={styles.navButton} onClick={goToPrev} aria-label="Previous video">
            &lt;
          </button>

          <div className={styles.singleVideoWrapper}>
            <video
              ref={videoRef}
              key={vid.f_id}
              src={vid.preview.video.video.url}
              poster={vid.preview.image.image.url}
              className={styles.videoPlayer}
              autoPlay
              playsInline
              controlsList="nofullscreen"
              controls
            />
            {fetchingMore && currentIndex >= videos.length - Math.floor(BATCH_SIZE * 0.3) && (
              <div className={styles.loadingMoreIndicator}>Loading more videos...</div>
            )}
          </div>

          <button className={styles.navButton} onClick={goToNext} aria-label="Next video">
            &gt;
          </button>
        </div>

        {/* Lyrics */}
        <div className={styles.lyricsContainer}>
          <p className={styles.lyricsText}>{vid.lyrics}</p>
        </div>
      </div>

      {/* Video Link & Date */}
      <a
        href={`https://fast.feelsmusic.app/feel/${vid.f_id}`}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.videoIdLabel}
      >
        {vid.f_id}
      </a>
      <div className={styles.dateLabel}>{formatDate(vid.date_created)}</div>

      {/* Debug Info */}
      <div className={styles.debugInfo}>
        Page {currentPage} of {totalPages} | Video {currentIndex + 1} of {videos.length} loaded | Cap {VIDEO_CAP} | {allVideosFetched ? 'All fetched' : 'More videos available'} | {isMuted ? 'Muted 🔇' : 'Unmuted 🔊'}
      </div>
    </div>
  );
};

export default FeelsShuffle;
